import { useEffect, useState } from 'react';

/** Matches the `.animate-thread-loaded` duration, plus a little slack. */
const HIGHLIGHT_MS = 1500;

const EMPTY: ReadonlySet<string> = new Set();

/**
 * The ids of a thread's items that just arrived (a loaded page, a new message),
 * for a brief highlight. The first batch after opening, or after `resetKey`
 * changes, is the thread itself and is not highlighted.
 */
export function useNewItemsHighlight(ids: string[], resetKey: unknown): ReadonlySet<string> {
  const [seen, setSeen] = useState<{ key: unknown; ids: ReadonlySet<string> }>(() => ({
    key: resetKey,
    ids: new Set(ids),
  }));
  const [fresh, setFresh] = useState<ReadonlySet<string>>(EMPTY);

  if (seen.key !== resetKey || seen.ids.size === 0 || ids.length === 0) {
    if (seen.key !== resetKey || ids.length !== seen.ids.size) {
      setSeen({ key: resetKey, ids: new Set(ids) });
      if (fresh.size > 0) setFresh(EMPTY);
    }
  } else {
    const added = ids.filter((id) => !seen.ids.has(id));
    if (added.length > 0) {
      setSeen({ key: resetKey, ids: new Set(ids) });
      setFresh(new Set([...fresh, ...added]));
    }
  }

  useEffect(() => {
    if (fresh.size === 0) return;
    const timer = setTimeout(() => setFresh(EMPTY), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [fresh]);

  return fresh;
}
